/**
 * TSGo — Enable Notifications button.
 *
 * Shared across all four PWAs (customer / store / rider / admin). Sits next
 * to the notification bell in every layout. States:
 *
 *   • Notifications supported? no → soft-hidden with tooltip.
 *   • Permission "default"      → "Enable" button (user-triggered flow).
 *   • Permission "granted"      → "Enabled" chip; opens debug popover.
 *   • Permission "denied"       → "Blocked" chip; explains browser reset.
 *
 * Everything else (VAPID, service-worker registration, token upload) is
 * delegated to `enablePush()` in features/push/firebase.ts — this component
 * only renders state.
 */

import { useEffect, useState } from "react";
import { BellRing, BellOff, ShieldAlert, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { enablePush, getPushStatus, type PushStatus } from "@/features/push/firebase";
import { cn } from "@/lib/utils";

export function NotificationEnableButton({ className }: { className?: string }) {
    const [status, setStatus] = useState<PushStatus | null>(null);
    const [busy, setBusy] = useState(false);
    const [debug, setDebug] = useState(false);

    const refresh = async () => setStatus(await getPushStatus());

    useEffect(() => {
        refresh();
        // Re-check when the tab regains focus (perm may have changed via
        // browser settings while we were away).
        const on = () => refresh();
        window.addEventListener("focus", on);
        return () => window.removeEventListener("focus", on);
    }, []);

    const onClick = async () => {
        if (!status) return;
        if (status.permission === "denied") {
            setDebug(true);
            return;
        }
        setBusy(true);
        const res = await enablePush();
        setBusy(false);
        await refresh();
        if (res.ok) toast.success("Notifications enabled");
        else if (res.reason === "permission-denied") toast.error("Permission blocked — enable it from browser settings");
        else if (res.reason === "not-configured") toast.error("Firebase not configured yet (ask super admin)");
        else if (res.reason === "unsupported-browser") toast.error("This browser doesn't support push");
        else if (res.reason === "no-token") toast.error("Couldn't obtain FCM token — check your VAPID key");
        else toast.error("Couldn't enable notifications");
    };

    if (!status) {
        return (
            <button
                type="button"
                disabled
                className={cn(
                    "inline-flex h-9 items-center gap-1 rounded-full border border-border/70 bg-background px-3 text-[11px] text-muted-foreground",
                    className,
                )}
            >
                <Loader2 className="h-3 w-3 animate-spin" /> Checking…
            </button>
        );
    }

    if (!status.supported) {
        return (
            <button
                type="button"
                title={status.supportReason || "Notifications not supported"}
                data-testid="push-enable-unsupported"
                className={cn(
                    "inline-flex h-9 items-center gap-1 rounded-full border border-border/70 bg-background px-3 text-[11px] text-muted-foreground",
                    className,
                )}
                onClick={() => setDebug(true)}
            >
                <BellOff className="h-3 w-3" /> Push unsupported
            </button>
        );
    }

    const label =
        status.permission === "granted"
            ? "Notifications on"
            : status.permission === "denied"
              ? "Blocked"
              : "Enable notifications";
    const Icon =
        status.permission === "granted"
            ? BellRing
            : status.permission === "denied"
              ? ShieldAlert
              : BellRing;

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                disabled={busy}
                data-testid="push-enable-button"
                title={`Permission: ${status.permission} · Firebase: ${
                    status.firebaseReady ? "configured" : "not configured"
                }`}
                className={cn(
                    "inline-flex h-9 items-center gap-1 rounded-full border px-3 text-[11px] font-bold transition",
                    status.permission === "granted"
                        ? "border-brand/40 bg-brand/5 text-brand"
                        : status.permission === "denied"
                          ? "border-red-200 bg-red-50 text-red-700"
                          : "border-border/70 bg-background text-foreground hover:border-brand hover:text-brand",
                    className,
                )}
            >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
                {label}
            </button>

            <button
                type="button"
                onClick={() => setDebug((v) => !v)}
                data-testid="push-debug-toggle"
                aria-label="Push debug"
                className="ml-1 grid h-9 w-9 place-items-center rounded-full border border-border/70 bg-background text-[10px] text-muted-foreground"
            >
                ⓘ
            </button>

            {debug ? (
                <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
                    <div className="w-full max-w-sm space-y-3 rounded-2xl border border-border bg-card p-4 shadow-elevated">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-extrabold">Push debug</p>
                            <button type="button" onClick={() => setDebug(false)}>
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <dl className="grid grid-cols-2 gap-y-1 text-xs">
                            <dt className="text-muted-foreground">Supported</dt>
                            <dd data-testid="push-debug-supported" className="font-mono">
                                {String(status.supported)}
                            </dd>
                            <dt className="text-muted-foreground">Permission</dt>
                            <dd data-testid="push-debug-permission" className="font-mono">
                                {status.permission}
                            </dd>
                            <dt className="text-muted-foreground">Firebase</dt>
                            <dd data-testid="push-debug-firebase" className="font-mono">
                                {status.firebaseReady ? "configured" : "not configured"}
                            </dd>
                            <dt className="text-muted-foreground">FCM token</dt>
                            <dd data-testid="push-debug-token" className="font-mono">
                                {status.tokenSaved ? "registered" : "not registered"}
                            </dd>
                        </dl>
                        {status.permission === "denied" ? (
                            <p className="text-xs text-red-700">
                                Notifications are blocked for this site. Open the padlock icon in
                                your browser's address bar, allow notifications, and reload.
                            </p>
                        ) : null}
                        {!status.supported ? (
                            <p className="text-xs text-muted-foreground">
                                {status.supportReason ||
                                    "Web push not available on this device / browser."}{" "}
                                On iOS: add the app to your Home Screen, open it from there, then
                                try again (requires iOS 16.4+).
                            </p>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </>
    );
}

export default NotificationEnableButton;
